import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import "./Navbar.css";
import { Method, API, Config } from "../config/Init.js"
import { listenToMessages } from "../config/firebase.js"
import { User } from 'lucide-react';

function TopNavbar({ toggleMobileMenu }) {
        const navigate = useNavigate();
        const location = useLocation();

        const [isProfileOpen, setIsProfileOpen] = useState(false);
        const [isNotifOpen, setIsNotifOpen] = useState(false);
        const [notifications, setNotifications] = useState([]);
        const [notifLoading, setNotifLoading] = useState(false);
        const [currentTime, setCurrentTime] = useState(new Date());
        const [darkMode, setDarkMode] = useState(false);
        const [isShopOpen, setIsShopOpen] = useState(true);
        const [showLogoutModal, setShowLogoutModal] = useState(false);
        const [isClosing, setIsClosing] = useState(false);
        const [hasUnreadNotif, setHasUnreadNotif] = useState(false);
        const [popup, setPopup] = useState({ visible: false, message: "", type: "", key: 0 });
        const popupTimerRef = useRef(null);

        const profileRef = useRef(null);
        const notifRef = useRef(null);

        const [adminState, setAdminState] = useState(() => Method.checkLoginStatus()?.data || {});
        const admindata = { status: adminState?.email ? 200 : 400, data: adminState };

        useEffect(() => {
                if (admindata.status !== 200) {
                        localStorage.removeItem("menus");
                        if (location.pathname !== '/login') {
                                navigate(`/login?page=${location.pathname}${location.search}`);
                        } else {
                                navigate("/login");
                        }
                        return;
                }
                setDarkMode(admindata.data.darkmodeaccess);
                setHasUnreadNotif(!!admindata.data.hasUnreadNotifications);
                Method.setCookie(
                        "theme",
                        admindata.data.darkmodeaccess ? "dark" : "light"
                );
                window.dispatchEvent(new Event("themeChanged"));
        }, [navigate]);

        useEffect(() => {
                const intervalId = setInterval(() => setCurrentTime(new Date()), 1000);
                return () => clearInterval(intervalId);
        }, []);

        useEffect(() => {
                const handleUnreadUpdate = (e) => {
                        if (e.detail && e.detail.hasUnread !== undefined) {
                                setHasUnreadNotif(e.detail.hasUnread);
                        }
                };
                window.addEventListener("unreadNotifUpdated", handleUnreadUpdate);
                return () => window.removeEventListener("unreadNotifUpdated", handleUnreadUpdate);
        }, []);

        useEffect(() => {
                const handleAdminDataUpdate = (e) => {
                        const updated = e.detail || Method.checkLoginStatus()?.data;
                        if (updated) {
                                setAdminState(updated);
                                if (updated.darkmodeaccess !== undefined) {
                                        setDarkMode(updated.darkmodeaccess);
                                }
                                if (updated.hasUnreadNotifications !== undefined) {
                                        setHasUnreadNotif(!!updated.hasUnreadNotifications);
                                }
                        }
                };
                window.addEventListener("adminDataUpdated", handleAdminDataUpdate);
                return () => window.removeEventListener("adminDataUpdated", handleAdminDataUpdate);
        }, []);

        useEffect(() => {
                function handleClickOutside(e) {
                        if (profileRef.current && !profileRef.current.contains(e.target)) {
                                setIsProfileOpen(false);
                        }
                        if (notifRef.current && !notifRef.current.contains(e.target)) {
                                setIsNotifOpen(false);
                        }
                }

                document.addEventListener("mousedown", handleClickOutside);
                return () => document.removeEventListener("mousedown", handleClickOutside);
        }, []);

        useEffect(() => {
                listenToMessages((payload) => {
                        setHasUnreadNotif(true);
                        const title = payload?.notification?.title || "New Notification";
                        const body = payload?.notification?.body || "You have a new message.";
                        Method.showPopup(setPopup, popupTimerRef, `${title}: ${body}`, "info");
                });
        }, []);

        const toggleDark = async () => {
                const empid = admindata.data._id;
                const current = Number(admindata.data.darkmodeaccess);
                const next = current === 0 ? 1 : 0;

                const res = await API.ChangeTheme(empid, next);

                if (res?.status === 200) {
                        setDarkMode(next);
                        window.dispatchEvent(new Event("themeChanged"));
                }
        };

        const closeModal = () => {
                setIsClosing(true);
                setTimeout(() => {
                        setShowLogoutModal(false);
                        setIsClosing(false);
                }, 250);
        };

        const handleConfirmAction = async () => {
                const response = await API.Logout();
                if (response.status === 200) {
                        window.location.href = "/login";
                }
                closeModal();
        };

        const updateUnreadIconState = (hasUnread) => {
                setHasUnreadNotif(!!hasUnread);
                if (admindata && admindata.data) {
                        admindata.data.hasUnreadNotifications = !!hasUnread;
                        Method.setCookie("admindata", admindata.data);
                }
                window.dispatchEvent(new CustomEvent("unreadNotifUpdated", { detail: { hasUnread: !!hasUnread } }));
        };

        const fetchNotifications = async () => {
                setNotifLoading(true);
                const res = await API.ListNotification(1, 10, { _id: -1 }, {}, {}, "");
                if (res?.status === 200) {
                        setNotifications(res.data || []);
                } else {
                        setNotifications([]);
                }
                setNotifLoading(false);
        };

        const markSingleNotificationRead = async (notificationId, clickaction, newTab = false) => {
                if (!notificationId) return;

                const res = await API.ReadNotification({ _id: notificationId, allread: 0 });
                if (res?.status === 200) {
                        setNotifications((prev) =>
                                prev.map((item) =>
                                        item._id === notificationId ? { ...item, read: 1 } : item
                                )
                        );
                        updateUnreadIconState(res.hasUnreadNotifications);
                        if (clickaction) {
                                if (newTab) {
                                        window.open(clickaction, '_blank');
                                } else {
                                        navigate(clickaction);
                                }
                        }
                }
        };

        const markAllNotificationsRead = async () => {
                const res = await API.ReadNotification({ allread: 1 });
                if (res?.status === 200) {
                        setNotifications((prev) => prev.map((item) => ({ ...item, read: 1 })));
                        updateUnreadIconState(false);
                }
        };

        const handleNotifToggle = async () => {
                const nextOpen = !isNotifOpen;
                setIsNotifOpen(nextOpen);
                if (nextOpen) {
                        await fetchNotifications();
                }
        };

        const getNotificationText = (n) => {
                return n?.message || n?.title || n?.description || n?.body || "Notification";
        };
        const getNotificationTime = (n) => {
                const raw = n?.createdat || n?.created_at || n?.date || n?.createdon;
                if (!raw) return "";
                const dt = new Date(raw);
                if (Number.isNaN(dt.getTime())) return "";
                return Method.formatDateTime(dt);
        };

        return (
                <>
                        <header className="top-navbar">
                                <button className="mobile-menu-btn" onClick={toggleMobileMenu}>
                                        <span className="material-symbols-outlined">menu</span>
                                </button>
                                <div className="top-navbar-inner">
                                        <div className="clock-box user-not-select">
                                                {Method.formatDateTime(currentTime)}
                                        </div>

                                        <div className="shop-status-box user-not-select">
                                                <span className={`status-dot ${isShopOpen ? "open" : "closed"}`}></span>
                                                <span className="status-text">{isShopOpen ? "Open" : "Closed"}</span>
                                                {Method.tooltip(
                                                        isShopOpen ? "Open" : "Closed",
                                                        <input
                                                                type="checkbox"
                                                                checked={isShopOpen}
                                                                className="chk-checkbox"
                                                                onChange={() => setIsShopOpen(o => !o)}
                                                        />,
                                                        "bottom"
                                                )}
                                        </div>

                                        <div className="notif-wrapper" ref={notifRef}>
                                                {Method.tooltip(
                                                        "Notifications",
                                                        <button
                                                                className="top-icon-btn user-not-select"
                                                                onClick={handleNotifToggle}
                                                        >
                                                                <span className="material-symbols-outlined">{hasUnreadNotif ? "notifications_unread" : "notifications"}</span>
                                                        </button>,
                                                        "bottom"
                                                )}

                                                {isNotifOpen && (
                                                        <div className="notifications-dropdown">
                                                                <div className="notif-header user-not-select">
                                                                        {/* <span className="material-symbols-outlined drop-item-icon main-color">notifications</span> */}
                                                                        <div className="fs-20 ml-5 main-color">Notifications</div>
                                                                        {Method.tooltip(
                                                                                "Mark as all Read",
                                                                                <span className="material-symbols-outlined main-color realall-notif mr-5" onClick={markAllNotificationsRead}>checklist</span>,
                                                                                'left'
                                                                        )}
                                                                </div>
                                                                <div className="drop-separator"></div>
                                                                {notifLoading ? (
                                                                        // <div className="notif-empty">Loading...</div>
                                                                        <div className="small-loader">
                                                                                {Method.showLoader()}
                                                                        </div>
                                                                ) : notifications.length > 0 ? (
                                                                        <div className="notif-list user-not-select">
                                                                                {notifications.map((n, idx) => {
                                                                                        const time = getNotificationTime(n);
                                                                                        return (
                                                                                                <div key={n._id || idx} className={`notif-item ${n.read === 0 ? "unread" : ""}`}>
                                                                                                        {n.read === 0 ?
                                                                                                                (<span className="material-symbols-outlined drop-item-icon main-color">notifications_unread</span>) :
                                                                                                                (<span className="material-symbols-outlined drop-item-icon">notifications</span>)
                                                                                                        }
                                                                                                        <div className="notif-content" onClick={() => markSingleNotificationRead(n._id, n.clickaction)} style={{ cursor: "pointer" }}>
                                                                                                                <div className="notif-text">{getNotificationText(n)}</div>
                                                                                                                {time ? <div className="notif-time">{time}</div> : null}
                                                                                                        </div>
                                                                                                        {n.read === 0 ?
                                                                                                                (<span className="material-symbols-outlined drop-item-icon" onClick={(e) => { e.stopPropagation(); markSingleNotificationRead(n._id, n.clickaction); }}>check</span>) :
                                                                                                                (<span className="material-symbols-outlined drop-item-icon main-color">done_all</span>)
                                                                                                        }
                                                                                                </div>
                                                                                        );
                                                                                })}
                                                                        </div>
                                                                ) : (
                                                                        <div className="notif-empty">No notifications</div>
                                                                )}
                                                                <div className="drop-separator"></div>
                                                                <NavLink className="notif-more user-not-select" to="/notifications">
                                                                        View All Notifications
                                                                </NavLink>
                                                        </div>
                                                )}
                                        </div>

                                        <div className="top-profile-wrapper user-not-select" ref={profileRef}>
                                                <div className="top-profile" onClick={() => setIsProfileOpen(o => !o)}>
                                                        <span className="main-color">
                                                                {Method.getInitials(admindata?.data?.name)}
                                                        </span>
                                                </div>

                                                {isProfileOpen && (
                                                        <div className="profile-dropdown">
                                                                <div className="profile-drop-header mb-5">
                                                                        <h3 className="profile-name">{admindata.data.name}</h3>
                                                                        <h4 className="profile-role">{admindata.data.role}</h4>
                                                                </div>
                                                                <button className="drop-item" onClick={() => navigate("/profile")}>
                                                                        <User className="drop-item-icon" />
                                                                        My Profile
                                                                </button>

                                                                <button className="drop-item" onClick={() => navigate("/settings")}>
                                                                        <span className="material-symbols-outlined drop-item-icon">settings</span>
                                                                        Settings
                                                                </button>

                                                                <button className="drop-item dark-mode-item" onClick={toggleDark} type="button">
                                                                        <span className="dark-mode-label">
                                                                                <span className="material-symbols-outlined drop-item-icon">
                                                                                        {darkMode ? "light_mode" : "dark_mode"}
                                                                                </span>
                                                                                {darkMode ? "Transparent Mode" : "Dark Mode"}
                                                                        </span>
                                                                        <span className={`dark-mode-switch ${darkMode ? "on" : ""}`}>
                                                                                <span className="dark-mode-thumb"></span>
                                                                        </span>
                                                                </button>

                                                                <div className="drop-separator"></div>

                                                                <button className="drop-item logout-btn" onClick={() => setShowLogoutModal(true)}>
                                                                        <span className="material-symbols-outlined drop-item-icon">logout</span>
                                                                        Logout
                                                                </button>
                                                        </div>
                                                )}
                                        </div>
                                </div>
                        </header>

                        {showLogoutModal && (
                                <div className="modal-overlay">
                                        <div className={`modal-content-select width-40 alert-modal ${isClosing ? "fade-out" : ""}`}>
                                                <span className="material-symbols-outlined modal-icon required fs-50">
                                                        logout
                                                </span>

                                                <h3 className="modal-title fs-25 required">Confirm Logout</h3>

                                                <p className="modal-description fs-20 required">
                                                        Are you sure you want to log out?
                                                </p>
                                                <p className="note required fs-15">This action cannot be undone.</p>

                                                <div>
                                                        <button className="main-cancle-btn mr-20" onClick={handleConfirmAction}>
                                                                Yes, Logout
                                                        </button>

                                                        <button className="main-btn ml-20" onClick={closeModal}>
                                                                Cancel
                                                        </button>
                                                </div>
                                        </div>
                                </div>
                        )}
                        {Method.renderPopup(popup, () => setPopup({ ...popup, visible: false }))}
                </>
        );
}

export default function Navbar() {
        const navigate = useNavigate();
        const location = useLocation();
        const [isPinned, setIsPinned] = useState(false);
        const [isHovered, setIsHovered] = useState(false);
        const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
        const [menus, setMenus] = useState([]);
        const [loading, setLoading] = useState(false);
        const [openSubmenus, setOpenSubmenus] = useState(new Set());

        useEffect(() => {
                const savedPinned = Method.getCookie("navbarPinned");
                if (savedPinned === "true") setIsPinned(true);
        }, []);

        useEffect(() => {
                const path = location.pathname || "/";
                const isDefaultRoute = Method.isPermissionBypassPath(path);

                const menuPaths = new Set();
                menus.forEach((m) => {
                        if (m.redirecturl) menuPaths.add(m.redirecturl);
                        (m.children || []).forEach((c) => {
                                if (c.redirecturl) menuPaths.add(c.redirecturl);
                        });
                });

                const isKnownMenuRoute = Array.from(menuPaths).some((p) => {
                        return path === p || path.startsWith(`${p}/`);
                });

                // Only enforce redirects for known routes; allow unknown URLs to show NotFound.
                if (!isDefaultRoute && !isKnownMenuRoute) return;

                const canView = Method.canAccess(path, "view");
                if (!canView && !Method.isPermissionBypassPath(path)) {
                        navigate("/dashboard");
                }
        }, [location.pathname, navigate, menus]);

        useEffect(() => {
                Method.setCookie("navbarPinned", isPinned, 360)
                if (isPinned /* || isHovered */) {
                        document.body.classList.add("navbar-pinned");
                } else {
                        document.body.classList.remove("navbar-pinned");
                }
        }, [isPinned, isHovered]);

	const organizeMenus = React.useCallback((menuData) => {
		if (!Array.isArray(menuData) || menuData.length === 0) return [];
		
		const perms = Method.getPermissions();
		const permMap = {};
		perms.forEach(p => {
			if (p.menu_id) permMap[p.menu_id.toString()] = p;
			if (p.menu_alias) permMap[p.menu_alias] = p;
		});

		const parents = menuData.filter(m => m.submenu === 0);
		const children = menuData.filter(m => m.submenu === 1);

		return parents
			.map(parent => {
				const parentPerm = permMap[parent._id?.toString()] || permMap[parent.redirecturl];
				const parentName = parentPerm?.menu_name || parent.name || "Menu";

				return {
					...parent,
					name: parentName,
					children: children
						.filter(c => c.parentmenuid === parent._id)
						.map(child => {
							const childPerm = permMap[child._id?.toString()] || permMap[child.redirecturl];
							const childName = childPerm?.menu_name || child.name || "Submenu";
							return {
								...child,
								name: childName
							};
						})
						.sort((a, b) => (parseInt(a.displayorder) || 0) - (parseInt(b.displayorder) || 0))
				};
			})
			.sort((a, b) => (parseInt(a.displayorder) || 0) - (parseInt(b.displayorder) || 0));
	}, []);

	const loadMenus = React.useCallback(async (freshData = null) => {
		if (freshData && Array.isArray(freshData) && freshData.length > 0) {
			setMenus(organizeMenus(freshData));
			return;
		}

		const cachedMenus = localStorage.getItem("menus");
		if (cachedMenus) {
			try {
				const parsed = JSON.parse(cachedMenus);
				if (Array.isArray(parsed) && parsed.length > 0) {
					setMenus(organizeMenus(parsed));
				}
			} catch (e) {}
		}

		try {
			const res = await API.fetchMenus(1, 99999, { displayorder: 1 }, { isactive: 1 }, {});
			if (res.status === 200) {
				const rawMenus = Array.isArray(res.data) ? res.data : res.data?.data || [];
				if (rawMenus.length > 0) {
					localStorage.setItem("menus", JSON.stringify(rawMenus));
					localStorage.setItem("menudetails", JSON.stringify(rawMenus));
					sessionStorage.setItem("menus", JSON.stringify(rawMenus));
					sessionStorage.setItem("menudetails", JSON.stringify(rawMenus));
					Method.setCookie("menus", rawMenus);
					Method.setCookie("menudetails", rawMenus);
					setMenus(organizeMenus(rawMenus));
				}
			}
		} catch (err) {
			console.error("Navbar fetchMenus error:", err);
		}
	}, [organizeMenus]);

	useEffect(() => {
		loadMenus();

		const handleMenusUpdated = (e) => {
			if (e.detail && Array.isArray(e.detail) && e.detail.length > 0) {
				loadMenus(e.detail);
			} else {
				loadMenus();
			}
		};

		const handlePermissionsUpdated = () => {
			loadMenus();
		};

		window.addEventListener("menusUpdated", handleMenusUpdated);
		window.addEventListener("permissionsUpdated", handlePermissionsUpdated);

		return () => {
			window.removeEventListener("menusUpdated", handleMenusUpdated);
			window.removeEventListener("permissionsUpdated", handlePermissionsUpdated);
		};
	}, [loadMenus]);

	// Auto-expand submenu if child page is active - React to route changes
	useEffect(() => {
		if (!isPinned && !isHovered) {
			setOpenSubmenus(new Set());
			return;
		}

		const currentPath = location.pathname;
		const newOpenSubmenus = new Set();

		menus.forEach(menu => {
			const hasActiveChild = menu.children?.some(child =>
				isPathMatch(currentPath, child.redirecturl)
			);

			if (hasActiveChild) {
				newOpenSubmenus.add(menu._id);
			}
		});

		setOpenSubmenus(newOpenSubmenus);
	}, [location.pathname, menus, isPinned, isHovered]);

        const isPathMatch = (currentPath, targetPath) => {
                if (!targetPath || targetPath === "#") return false;

                if (currentPath === targetPath) return true;

                if (currentPath.startsWith(targetPath + "/")) return true;

                return false;
        };

        const toggleSubmenu = (id, hasChildren) => {
                if (!hasChildren) return;
                setOpenSubmenus(prev => {
                        const newSet = new Set(prev);
                        newSet.has(id) ? newSet.delete(id) : newSet.add(id);
                        return newSet;
                });
        };

        const isMenuActive = (menu) => {
                const currentPath = location.pathname;

                if (isPathMatch(currentPath, menu.redirecturl)) return true;

                return menu.children?.some(child => isPathMatch(currentPath, child.redirecturl)) || false;
        };

        const isChildMenuActive = (childUrl) => {
                return isPathMatch(location.pathname, childUrl);
        };

        const handleMenuClick = (menu, e) => {
                const hasChildren = menu.children?.length > 0;
                if (hasChildren) {
                        if (e?.preventDefault) e.preventDefault();
                        if (!isPinned && !isHovered) return;
                        toggleSubmenu(menu._id, true);
                } else {
                        setIsMobileMenuOpen(false);
                }
        };


        const isExpanded = isPinned || isHovered || isMobileMenuOpen;

        const [permissions, setPermissionsState] = useState(() => Method.getPermissions());

        useEffect(() => {
                const handlePermissionsUpdate = (e) => {
                        const perms = e.detail || Method.getPermissions();
                        setPermissionsState(perms);
                };
                window.addEventListener("permissionsUpdated", handlePermissionsUpdate);
                return () => window.removeEventListener("permissionsUpdated", handlePermissionsUpdate);
        }, []);
        const canViewMenu = (path) => Method.canAccess(path, "view");
        const filterMenusByPermission = (menuList) => {
                if (!permissions.length) {
                        return menuList.filter((m) => isPathMatch("/dashboard", m.redirecturl) || m.name?.toLowerCase() === "dashboard");
                }
                const menudata = menuList.map((m) => {
                        const children = (m.children || []).filter((c) => canViewMenu(c.redirecturl));
                        const canSeeParent = canViewMenu(m.redirecturl) || children.length > 0;
                        return canSeeParent ? { ...m, children } : null;
                })
                        .filter(Boolean)
                return menudata;
        };

        return (
                <>
                        <TopNavbar toggleMobileMenu={() => setIsMobileMenuOpen(true)} />

                        {isMobileMenuOpen && (
                                <div className="mobile-overlay active" onClick={() => setIsMobileMenuOpen(false)}></div>
                        )}

                        <aside
                                className={`navbar ${isPinned ? "pinned" : ""} ${isMobileMenuOpen ? "mobile-open" : ""}`}
                                onMouseEnter={() => setIsHovered(true)}
                                onMouseLeave={() => setIsHovered(false)}
                        >
                                <div className="navbar-top">
                                        <div className={`logo-stack ${isExpanded ? "open" : ""}`}>
                                                <img
                                                        src={Config.moonlightcafelogosquare}
                                                        alt="Moonlight Cafe"
                                                        className="logo logo-square"
                                                />
                                                <img
                                                        src={Config.moonlightcafetext}
                                                        alt="Moonlight Cafe"
                                                        className="logo logo-landscape"
                                                />
                                        </div>

                                        <div
                                                className={`toggle-btn desktop-toggle ${isPinned ? "active" : ""}`}
                                                onClick={() => setIsPinned(!isPinned)}
                                        >
                                                <span className="material-symbols-outlined">
                                                        {isPinned ? "adjust" : "circle"}
                                                </span>
                                        </div>

                                        <button
                                                type="button"
                                                className="sidebar-close-btn mobile-close-btn"
                                                onClick={() => setIsMobileMenuOpen(false)}
                                        >
                                                <span className="material-symbols-outlined fs-20">close</span>
                                        </button>
                                </div>

                                <nav className="navbar-center">
                                        {!loading && menus.length > 0 ? (
                                                filterMenusByPermission(menus).map(menu => {
                                                        const hasChildren = menu.children?.length > 0;
                                                        const isSubmenuOpen = openSubmenus.has(menu._id);
                                                        const isActive = isMenuActive(menu);

                                                        return (
                                                                <div key={menu._id} className="menu-item-container">
                                                                        {hasChildren ? (
                                                                                <button
                                                                                        type="button"
                                                                                        className={`menu-item ${isActive ? "active" : ""} has-children`}
                                                                                        onClick={(e) => handleMenuClick(menu, e)}
                                                                                >
                                                                                        <span className="menu-icon material-symbols-outlined">
                                                                                                {menu.icon || "menu"}
                                                                                        </span>
                                                                                        <span className="menu-text">{menu.name}</span>
                                                                                        <span className={`submenu-arrow material-symbols-outlined ${isSubmenuOpen ? "open" : ""}`}>
                                                                                                keyboard_arrow_right
                                                                                        </span>
                                                                                </button>
                                                                        ) : (
                                                                                <NavLink
                                                                                        to={menu.redirecturl || "/"}
                                                                                        className={`menu-item ${isActive ? "active" : ""}`}
                                                                                        onClick={(e) => handleMenuClick(menu, e)}
                                                                                >
                                                                                        <span className="menu-icon material-symbols-outlined">
                                                                                                {menu.icon || "menu"}
                                                                                        </span>
                                                                                        <span className="menu-text">{menu.name}</span>
                                                                                </NavLink>
                                                                        )}

                                                                        {hasChildren && (
                                                                                <div className={`submenu ${isSubmenuOpen ? "open" : ""}`}>
                                                                                        {menu.children.map(child => (
                                                                                                <NavLink
                                                                                                        key={child._id}
                                                                                                        to={child.redirecturl || "/"}
                                                                                                        onClick={() => setIsMobileMenuOpen(false)}
                                                                                                        className={`submenu-item ${isChildMenuActive(child.redirecturl) ? "active" : ""}`}
                                                                                                >
                                                                                                        <span className="submenu-icon material-symbols-outlined">
                                                                                                                {child.icon || "fiber_manual_record"}
                                                                                                        </span>
                                                                                                        <span className="submenu-text">{child.name}</span>
                                                                                                </NavLink>
                                                                                        ))}
                                                                                </div>
                                                                        )}
                                                                </div>
                                                        );
                                                })
                                        ) : (
                                                <div className="menu-item-container">
                                                        <NavLink
                                                                to="/dashboard"
                                                                onClick={() => setIsMobileMenuOpen(false)}
                                                                className={`menu-item ${location.pathname === "/dashboard" ? "active" : ""}`}
                                                        >
                                                                <span className="menu-icon material-symbols-outlined">home</span>
                                                                <span className="menu-text">Dashboard</span>
                                                        </NavLink>
                                                </div>
                                        )}
                                </nav>
                        </aside>
                </>
        );
}
